/*!
 * reverb.js — reverb:{mix, time, damp, pre} for synths and audio files
 * © 2026 Rob Canning — GPLv3
 *
 *   mix    0..1, how much of the room is heard (0.2). Equal power: the dry
 *          sound gives way as the room comes up, without a dip at 0.5
 *   time   seconds for the tail to die away (to -60 dB) below damp (1.8)
 *   damp   Hz: above it the tail dies four times as fast, so the room
 *          darkens as it decays -- 600 a heavy curtain, 8000 tiles (1800)
 *   pre    seconds before the room answers (0.015): a little keeps the
 *          attack clear of the wash
 *
 * A convolution reverb: the room is a made impulse response -- a few early
 * reflections, then noise whose lows decay over `time` and highs over a
 * quarter of it -- played against the sound by a ConvolverNode. Responses
 * are made once per setting and shared: a buffer is only read.
 *
 * A ConvolverNode takes two channels at most: a reverb wider than that (an
 * fx() bus on speakers(quad)) is a room for each pair of outputs, each of
 * its own noise, so a sound's room is where the sound is.
 */

import { evalMaybeRandom } from "./audioShared.js";
import { roomResponse } from "./reverbRoom.js";

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const num = (v, fallback) => {
  const n = Number(evalMaybeRandom(v));
  return Number.isFinite(n) ? n : fallback;
};

/** The settings in a reverb:{...}, resolved and kept in range. */
export function reverbSettings(cfg = {}) {
  return {
    mix: clamp(num(cfg.mix ?? cfg.amount, 0.2), 0, 1),
    time: clamp(num(cfg.time, 1.8), 0.05, 10),
    damp: clamp(num(cfg.damp, 1800), 200, 16000),
    pre: clamp(num(cfg.pre ?? cfg.predelay, 0.015), 0, 0.5),
  };
}

const cache = new Map();           // key -> AudioBuffer
const CACHE_MAX = 32;

/** The room for these settings, as an AudioBuffer: made once, then shared. `variant`: another of the same (the next pair's). */
export function reverbIR(ctx, s, variant = 0) {
  const sr = ctx.sampleRate;
  const key = `${sr}|${s.time.toFixed(3)}|${Math.round(s.damp)}|${s.pre.toFixed(3)}|${variant}`;
  const hit = cache.get(key);
  if (hit) { cache.delete(key); cache.set(key, hit); return hit; }
  const [l, r] = roomResponse(sr, s);
  const ir = ctx.createBuffer(2, l.length, sr);
  ir.copyToChannel(l, 0);
  ir.copyToChannel(r, 1);
  if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
  cache.set(key, ir);
  return ir;
}

const law = mix => ({ dry: Math.cos(mix * Math.PI / 2), wet: Math.sin(mix * Math.PI / 2) });

// the room: { conv (the way in), level (the way out), nodes }
function roomNode(ctx, s, channels = 2) {
  const convolver = variant => {
    const c = ctx.createConvolver();
    c.normalize = true;
    c.buffer = reverbIR(ctx, s, variant);
    return c;
  };
  const level = ctx.createGain();
  if (channels <= 2) {
    const conv = convolver(0);
    conv.connect(level);
    return { conv, level, nodes: [conv, level] };
  }
  // a pair at a time: split, each pair through its own room, merged again
  const conv = new GainNode(ctx, { channelCount: channels, channelCountMode: "explicit", channelInterpretation: "discrete" });
  const split = new ChannelSplitterNode(ctx, { numberOfOutputs: channels });
  const merge = new ChannelMergerNode(ctx, { numberOfInputs: channels });
  const nodes = [conv, split, merge, level];
  conv.connect(split);
  for (let c = 0; c < channels; c += 2) {
    const pairIn = new ChannelMergerNode(ctx, { numberOfInputs: 2 });
    const pairOut = new ChannelSplitterNode(ctx, { numberOfOutputs: 2 });
    const room = convolver(c / 2);
    split.connect(pairIn, c, 0);
    if (c + 1 < channels) split.connect(pairIn, c + 1, 1);
    pairIn.connect(room).connect(pairOut);
    pairOut.connect(merge, 0, c);
    if (c + 1 < channels) pairOut.connect(merge, 1, c + 1);
    nodes.push(pairIn, room, pairOut);
  }
  merge.connect(level);
  return { conv, level, nodes };
}

const disconnectRoom = room => { for (const n of room?.nodes || []) { try { n.disconnect(); } catch { /* gone */ } } };

/**
 * A reverb: { input, dry, wet, ... }. Send the sound to input (the room)
 * and to dry (itself); dry and wet go on to the same place. `channels`:
 * more than two, a room for each pair (an fx() bus on more speakers).
 */
export function buildReverb(ctx, cfg, channels = 2) {
  if (!cfg) return null;
  const s = reverbSettings(cfg);
  const input = ctx.createGain();
  const dry = ctx.createGain();
  const wet = ctx.createGain();
  const g = law(s.mix);
  dry.gain.value = g.dry;
  wet.gain.value = g.wet;
  const room = roomNode(ctx, s, channels);
  input.connect(room.conv);
  room.level.connect(wet);
  return { input, dry, wet, room, channels, settings: s, time: s.pre + s.time };
}

/**
 * A reverb:{...} run again on a sound already playing: the mix glides; a
 * new time, damp or pre crossfades to the new room while the old one rings out.
 */
export function updateReverb(rv, cfg, ramp = 0.05) {
  if (!rv || !cfg) return;
  const ctx = rv.input.context;
  const now = ctx.currentTime;
  const s = reverbSettings({ ...rv.settings, ...cfg });
  if (cfg.mix != null || cfg.amount != null) {
    const g = law(s.mix);
    for (const [node, v] of [[rv.dry, g.dry], [rv.wet, g.wet]]) {
      node.gain.cancelScheduledValues(now);
      node.gain.setValueAtTime(node.gain.value, now);
      node.gain.linearRampToValueAtTime(v, now + ramp);
    }
  }
  const old = rv.settings;
  if (s.time !== old.time || s.damp !== old.damp || s.pre !== old.pre) {
    const next = roomNode(ctx, s, rv.channels);
    next.level.gain.setValueAtTime(0, now);
    next.level.gain.linearRampToValueAtTime(1, now + 0.15);
    rv.input.connect(next.conv);
    next.level.connect(rv.wet);
    const prev = rv.room;
    try { rv.input.disconnect(prev.conv); } catch { /* gone */ }
    // the old room keeps ringing what it already heard, then goes
    setTimeout(() => disconnectRoom(prev),
      (old.pre + old.time + 0.5) * 1000);
    rv.room = next;
    rv.time = s.pre + s.time;
  }
  rv.settings = s;
}

/** Take a reverb apart (the voice is done with it). */
export function disconnectReverb(rv) {
  if (!rv) return;
  for (const n of [rv.input, rv.dry, rv.wet]) {
    try { n?.disconnect(); } catch { /* gone */ }
  }
  disconnectRoom(rv.room);
}
